/** @jsxImportSource @emotion/react */
import { Modal } from "../../../components/Modal";
import { css } from "@emotion/react";
import {
  useController,
  UseFieldArrayRemove,
  UseFieldArrayUpdate,
  useForm,
} from "react-hook-form";
import {
  OverwriteMenuModalFormValues,
  OverwriteMenuModalProduct,
} from "./OverwriteMenuModal";
import { PriceInput } from "../../../components/forms/inputs/PriceInput";
import { formatMoneyAmount } from "../../../util";
import { ButtonAction, ButtonPrimary } from "../../../components/NewButtons";
import { defaultImage } from "./MenuProductItem";
import { MenuOverwriteButtonRow } from "./MenuOverwriteButtonRow";
import { TextField } from "../../../components/forms/fields/TextField";
import { OverwriteType } from "../models/Menus";
import { MenuOverwriteActionLabel } from "./MenuOverwriteActionLabel";
import { SwitchField } from "../../../components/forms/fields/SwitchField";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  product: OverwriteMenuModalProduct;
  actualProduct: OverwriteMenuModalProduct;
  isOnOriginalMenu: boolean;
  remove: UseFieldArrayRemove;
  update: UseFieldArrayUpdate<OverwriteMenuModalFormValues, "products">;
};

const buttonsWrapperStyle = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const imageContainerStyle = css`
  position: relative;
  width: 100%;
  height: 300px;
`;

const bodyContainerStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const buttonPairWrapperStyle = css`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
`;

export const MenuOverwriteProductModal = ({
  isOpen,
  onClose,
  product,
  isOnOriginalMenu,
  remove,
  actualProduct,
  update,
}: Props) => {
  const methods = useForm<{
    priceValueDelivery: string | number;
    priceValue: string | number;
    title: string;
    isHidden: boolean;
    isUnavailable: boolean;
    priceDescription: string | null;
    hidePriceDescription: boolean;
  }>({
    defaultValues: {
      priceValueDelivery:
        product.overwriteValues.priceValueDelivery ||
        product.overwriteValues.priceValueDelivery === 0
          ? product.overwriteValues.priceValueDelivery
          : "",
      priceValue:
        product.overwriteValues.priceValue ||
        product.overwriteValues.priceValue === 0
          ? product.overwriteValues.priceValue
          : "",
      title: product.overwriteValues.title || "",
      isHidden: product.overwriteValues.isHidden,
      isUnavailable: product.overwriteValues.isUnavailable,
      priceDescription: product.overwriteValues.priceDescription,
      hidePriceDescription:
        product.overwriteValues.priceDescription === "" &&
        product.overwriteValues.priceDescription !== undefined,
    },
  });

  const overwriteTitle = methods.watch(`title`);
  const overwritePrice = methods.watch(`priceValue`);
  const overwritePriceDelivery = methods.watch(`priceValueDelivery`);
  const overwritePriceDescription = methods.watch(`priceDescription`);

  const {
    ref: titleValueRef,
    onChange: titleValueOnChange,
    ...titleValueRest
  } = methods.register("title");

  const {
    field: {
      onChange: priceDescriptionOnChange,
      value: priceDescriptionValue,
      ref: priceDescriptionValueRef,
      ...priceDescriptionValueRest
    },
  } = useController({
    name: "priceDescription",
    control: methods.control,
  });

  const {
    field: { onChange: isHiddenOnChange, value: isHiddenValue },
  } = useController({
    name: "isHidden",
    control: methods.control,
  });

  const {
    field: { value: hidePriceDescriptionValue, ...hidePriceDescriptionRest },
  } = useController({
    name: "hidePriceDescription",
    control: methods.control,
  });

  const {
    field: { onChange: isUnavailableOnChange, value: isUnavailableValue },
  } = useController({
    name: "isUnavailable",
    control: methods.control,
  });

  const imageStyle = css`
    width: 100%;
    height: 300px;
    object-fit: cover;
    border-radius: 8px;
    ${(isHiddenValue || isUnavailableValue) &&
    `
    filter: blur(2px);
    `}
  `;

  const handleClose = () => {
    methods.reset();
    onClose();
  };

  return (
    <Modal closable={false} isOpen={isOpen} onClose={handleClose}>
      <div css={imageContainerStyle}>
        {product.imageUrl ? (
          <img css={imageStyle} src={product.imageUrl} alt={product.name} />
        ) : (
          <img css={imageStyle} src={defaultImage} alt={product.name} />
        )}
        {(isHiddenValue ||
          isUnavailableValue ||
          !isOnOriginalMenu ||
          overwriteTitle ||
          overwritePriceDescription ||
          hidePriceDescriptionValue ||
          overwritePrice ||
          overwritePriceDelivery) && (
          <MenuOverwriteActionLabel
            onButtonClick={() => {
              update(product.fieldIndex!, {
                ...actualProduct,
                overwriteValues: {
                  type:
                    product.overwriteValues.type === OverwriteType.ADD
                      ? OverwriteType.ADD
                      : OverwriteType.NONE,
                  isHidden: false,
                  isUnavailable: false,
                  title: "",
                  priceValue: "",
                  priceValueDelivery: "",
                  priceDescription: undefined,
                },
              });
              handleClose();
            }}
            overwritePriceDescription={overwritePriceDescription as string}
            isHiddenValue={isHiddenValue}
            isUnavailableValue={isUnavailableValue}
            hidePriceDescription={hidePriceDescriptionValue}
            overwriteTitle={overwriteTitle}
            overwritePrice={overwritePrice as string}
            overwritePriceDelivery={overwritePriceDelivery as string}
            product={product}
            isOnOriginalMenu={isOnOriginalMenu}
          />
        )}
      </div>
      <div css={bodyContainerStyle}>
        <TextField
          label="Overwrite title"
          helperText="You can overwrite the title of the product here"
          errorMessage={methods.getFieldState("title").error?.message}
          placeholder={product.title}
          innerRef={titleValueRef}
          onChange={titleValueOnChange}
          {...titleValueRest}
        />
        <TextField
          label="Overwrite price description"
          helperText="You can overwrite the price description of the product here"
          errorMessage={
            methods.getFieldState("priceDescription").error?.message
          }
          value={priceDescriptionValue || undefined}
          placeholder={product.priceDescription || "Enter price description"}
          innerRef={priceDescriptionValueRef}
          onChange={priceDescriptionOnChange}
          disabled={hidePriceDescriptionValue}
          {...priceDescriptionValueRest}
        />
        {product.priceDescription && (
          <SwitchField
            label="Hide price description"
            checked={hidePriceDescriptionValue}
            {...hidePriceDescriptionRest}
          />
        )}
        <PriceInput
          label="Overwrite price"
          formMethods={methods}
          helpertext="You can overwrite the price of the product here"
          placeholder={formatMoneyAmount(
            product.price.amount,
            product.price.currency
          )}
          currencyString={product.price.currency}
          name="priceValue"
        />
        <PriceInput
          label="Overwrite delivery price"
          formMethods={methods}
          helpertext="You can overwrite the delivery price of the product here"
          placeholder={formatMoneyAmount(
            product.priceDelivery.amount,
            product.priceDelivery.currency
          )}
          currencyString={product.priceDelivery.currency}
          name="priceValueDelivery"
        />
        <div css={buttonsWrapperStyle}>
          <MenuOverwriteButtonRow
            hiddenValue={isHiddenValue}
            isOnOriginalMenu={isOnOriginalMenu}
            unavailableValue={isUnavailableValue}
            onHiddenChange={isHiddenOnChange}
            onUnavailableChange={isUnavailableOnChange}
            remove={remove}
            product={product}
          />
          <div css={buttonPairWrapperStyle}>
            <ButtonPrimary type="button" onClick={handleClose}>
              Cancel
            </ButtonPrimary>
            <ButtonAction
              type="button"
              onClick={() => {
                methods.handleSubmit((data) => {
                  const {
                    hidePriceDescription,
                    priceDescription,
                    ...rest
                  } = data;
                  update(product.fieldIndex!, {
                    ...actualProduct,
                    overwriteValues: {
                      ...product.overwriteValues,
                      ...rest,
                      priceDescription: hidePriceDescriptionValue
                        ? ""
                        : priceDescription || undefined,
                      type:
                        product.overwriteValues.type === OverwriteType.ADD
                          ? OverwriteType.ADD
                          : OverwriteType.CHANGE,
                    },
                  });
                  handleClose();
                })();
              }}
            >
              Save
            </ButtonAction>
          </div>
        </div>
      </div>
    </Modal>
  );
};
